import { useApiQuery } from '../../../shared/hooks/useApiQuery';
import {
  voiceCreditApi,
  vcToMinutes,
  tierName,
  type VoiceBalance,
  type VoiceAddonStatus,
  type VoiceTelephonyUsage,
} from '../services/voiceCreditApi';

const THIRTY_SECONDS = 30 * 1000;

/** First day of next month at 00:00 (next allowance reset), as ISO. */
function nextMonthResetIso(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString();
}

/**
 * Derived, MINUTES-first view of the voice add-on (Frente C).
 *
 * VC stays internal — every user-facing figure is in minutes
 * (`available_vc / 2`). The model also exposes the active tier, concurrency,
 * the percent consumed, low/empty flags (fail-closed), and the next reset.
 */
export interface VoiceStatusModel {
  /** Tenant has an active voice tier add-on. */
  active: boolean;
  /** Tier slug ('voz.pro' …) when active. */
  tierSlug?: string;
  /** Human tier label ('Voz Pro') when active. */
  tierLabel: string;
  /** Monthly tier price (BRL) when active. */
  priceBrl?: number;
  /** Max simultaneous calls allowed by the tier. */
  maxConcurrent?: number;
  /** Spendable minutes right now (available_vc / 2). */
  minutesRemaining: number;
  /** Included minutes per cycle (allowance_vc / 2). */
  minutesIncluded: number;
  /** Percent of included minutes CONSUMED (0–100). */
  percentConsumed: number;
  /** True when < 20% of included minutes remain (but not empty). */
  isLow: boolean;
  /** True when 0 minutes remain — fail-closed, dialing stops. */
  isEmpty: boolean;
  /** ISO timestamp of the next monthly allowance reset. */
  nextResetIso: string;
}

function buildModel(
  balance: VoiceBalance | null,
  addon: VoiceAddonStatus | null,
): VoiceStatusModel {
  const active = addon?.active === true;
  const minutesRemaining = vcToMinutes(balance?.available_vc);
  const minutesIncluded = vcToMinutes(addon?.allowance_vc);

  // Percent consumed against the included allowance. If we don't know the
  // allowance (no tier), fall back to 0 so the bar stays empty rather than NaN.
  const percentConsumed =
    minutesIncluded > 0
      ? Math.min(100, Math.max(0, Math.round((1 - minutesRemaining / minutesIncluded) * 100)))
      : 0;

  const isEmpty = active && minutesRemaining <= 0;
  const isLow =
    active && !isEmpty && minutesIncluded > 0 && minutesRemaining / minutesIncluded < 0.2;

  return {
    active,
    tierSlug: addon?.tier_slug,
    tierLabel: tierName(addon?.tier_slug),
    priceBrl: addon?.price_brl,
    maxConcurrent: addon?.max_concurrent,
    minutesRemaining,
    minutesIncluded,
    percentConsumed,
    isLow,
    isEmpty,
    nextResetIso: nextMonthResetIso(),
  };
}

/**
 * Voice add-on status (Frente C).
 *
 * Combines the voice pool balance (spendable VC → minutes) with the active
 * tier add-on snapshot (tier, allowance, concurrency, price). Both endpoints
 * return 404 when the voice feature flag is OFF — the services map that to
 * `null` (no toast). `isVoiceEnabled` stays `false` until both resolve.
 *
 * Returns:
 * - `model` — the MINUTES-first derived view (see VoiceStatusModel).
 * - `balance` / `addon` — raw snapshots (VC kept for dev tooltips only).
 * - `isVoiceEnabled` — false when the feature is off for this tenant.
 */
export function useVoiceCreditStatus() {
  const balanceQ = useApiQuery<VoiceBalance | null>(
    ['voice-credits', 'balance'],
    () => voiceCreditApi.getVoiceBalance(),
    {
      staleTime: THIRTY_SECONDS,
      refetchInterval: THIRTY_SECONDS,
      retry: 1,
    },
  );

  const addonQ = useApiQuery<VoiceAddonStatus | null>(
    ['voice-credits', 'addon-status'],
    () => voiceCreditApi.getVoiceAddonStatus(),
    {
      staleTime: THIRTY_SECONDS,
      refetchInterval: THIRTY_SECONDS,
      retry: 1,
    },
  );

  // Telephony consumed this calendar month (Frente C gap 1). Independent of the
  // balance/addon queries — 404 (feature off) maps to `null` (no toast).
  const telephonyQ = useApiQuery<VoiceTelephonyUsage | null>(
    ['voice-credits', 'telephony-usage'],
    () => voiceCreditApi.getTelephonyUsage(),
    {
      staleTime: THIRTY_SECONDS,
      refetchInterval: THIRTY_SECONDS,
      retry: 1,
    },
  );

  const isLoading = balanceQ.isLoading || addonQ.isLoading;
  const error = balanceQ.error || addonQ.error;

  // `data === undefined` é "não TENHO a leitura" — em voo ou depois de uma
  // falha sem cache. `null` é diferente: é o 404 MEDIDO (voz desligada).
  const balanceLido = balanceQ.data !== undefined;
  const addonLido = addonQ.data !== undefined;

  // Voice is "enabled" for the tenant when the feature flag is on — i.e. the
  // BFF did NOT 404. The balance returning a non-null payload is the signal
  // (the add-on status is non-null even when the tenant has no tier:
  // `{ active: false }`).
  //
  // Deliberadamente NÃO lê `error`: uma falha de rede não é uma medição de
  // que a voz está desligada. Com cache (uma leitura boa seguida de um poll
  // de 30s que falhou) o dado velho continua valendo; sem cache a resposta
  // certa é `leituraFalhou`, nunca "não habilitada".
  const isVoiceEnabled = !isLoading && balanceQ.data != null;

  // A terceira resposta, que nem `isVoiceEnabled` nem `model.active` sabem
  // dar: o saldo não foi lido, OU a voz está ligada (saldo não-nulo) e o
  // add-on não foi lido. Sem isto, saldo em falha virava "Voz não habilitada"
  // e add-on em falha virava "Sem plano de voz / Contratar voz" — as duas são
  // afirmações sobre o CONTRATO do cliente feitas sobre a nossa ignorância.
  // Saldo MEDIDO como `null` (404) decide sozinho: a voz está desligada, e a
  // falha do add-on não muda esse fato.
  const leituraFalhou = !isLoading && (!balanceLido || (balanceQ.data != null && !addonLido));

  const model = buildModel(balanceQ.data ?? null, addonQ.data ?? null);

  const refetch = () => {
    balanceQ.refetch();
    addonQ.refetch();
    telephonyQ.refetch();
  };

  return {
    model,
    balance: balanceQ.data ?? null,
    addon: addonQ.data ?? null,
    /** Telephony consumed this calendar month (minutes + R$); null when off. */
    telephonyUsage: telephonyQ.data ?? null,
    telephonyLoading: telephonyQ.isLoading,
    isVoiceEnabled,
    /** Saldo (ou, com a voz ligada, o add-on) não pôde ser lido — nem "desligada" nem "sem plano". */
    leituraFalhou,
    isLoading,
    error,
    refetch,
  };
}
